<template>
  <div class="search-hint pb-3 rounded bg-white select-none">
    <template v-if="!hasSuggestion">
      <div v-if="popularKeywordList?.length" class="top-search">
        <div class="title py-4 px-5">
          <span class="text-base text-gray-80">{{ $t('search.popular') }}</span>
        </div>
        <div class="keyword-list flex gap-2 flex-wrap px-5 py-4">
          <template v-for="k in popularKeywordList" :key="k">
            <etg-button class="text-left" type="secondary" size="small" truncate-text @click="onPopularKeywordClick(k)">
              {{ k }}
            </etg-button>
          </template>
        </div>
      </div>
      <div class="history">
        <div class="title px-5 flex justify-between items-center">
          <span class="text-base text-gray-80 py-4">{{ $t('search.recent') }}</span>
          <etg-button v-if="recordList.length" type="text" size="small" class="active" @click="handleClearSearchRecord">
            {{ $t('search.recent.clear') }}
          </etg-button>
        </div>
        <div class="keyword-list flex flex-col md:max-h-432px scrollbar">
          <template v-for="item in recordList" :key="item.context">
            <div class="record hover:bg-gray-20 active:bg-gray-40 transition-colors px-5 py-4 cursor-pointer" @click="onHistoryKeywordClick(item)">
              <div class="flex items-center">
                <svg-icon v-if="item.type === SearchType.KEYWORD" name="time" class="size-8 text-black mr-2" />
                <img v-else :src="item.image" class="w-8 h-8 mr-2 rounded" />
                <span class="text-base line-clamp-1">{{ item.text }}</span>
              </div>
            </div>
          </template>
          <scroll-mask />
        </div>
        <div v-if="!recordList.length" class="px-5 py-4">
          <span class="text-gray-60 text-base">{{ $t('search.recent.empty.content') }}</span>
        </div>
      </div>
    </template>
    <div v-else class="keyword-list flex flex-col md:max-h-432px scrollbar">
      <template v-for="(item, index) in suggestionUIList" :key="item.context">
        <div
          ref="suggestionRefs"
          class="record hover:bg-gray-20 active:bg-gray-40 transition-colors px-5 py-4 cursor-pointer"
          @click="onSuggestionClick(item)"
        >
          <div class="flex items-center">
            <svg-icon v-if="index === 0" name="search" class="size-8 text-black mr-2" />
            <img v-else :src="item.image" class="w-8 h-8 mr-2 rounded" />
            <span v-highlight="[item.text!, index === 0 ? '' : keyword]" class="suggestion-text text-base line-clamp-1">{{ item.text }}</span>
          </div>
        </div>
      </template>
      <scroll-mask />
    </div>
  </div>
</template>

<script setup lang="ts">
import { getSearchKeywords, getSearchSuggestions } from '@/service/search'
import { AsyncDataKeys } from '@/constants/async-data-keys'

import { useRegionStore } from '@/store/region'
import type { ElasticsearchCompleteKeyword } from '@/types/api'
import { SearchType, type SearchRecordItem, type SearchClickPayload } from '@/types/search'
import { SearchKeywordSource } from '@/types/gtm'

interface Emits {
  (event: 'search-click', payload: SearchClickPayload): void
  (event: 'clear', hasAnyHint: boolean): void
}

type Props = {
  keyword: string
}

const emit = defineEmits<Emits>()
const props = defineProps<Props>()

const config = useRuntimeConfig()

const regionStore = useRegionStore()

const { tagId, categoryId } = useCategory()
const searchRecord = useSearchRecord()

const { clear: clearHighlight, vHighlight } = useTextHighlight()
const isOnline = useOnline()

const recordList = computed(() => searchRecord.getRecordsByKey(tagId.value ?? categoryId.value))

const { data: popularKeywordList } = useAsyncData(
  useAsyncDataKey(AsyncDataKeys.SEARCH_POPULAR_KEYWORDS, [categoryId.value, regionStore.currentRegion?.region_id]),
  async () => {
    const apiParams = categoryId.value ? { category_id: categoryId.value } : { region_id: `${regionStore.currentRegion?.region_id}` }
    const { data } = await getSearchKeywords(apiParams)
    return data.list
  }
)

const { data: suggestionList } = useAsyncData(
  useAsyncDataKey(AsyncDataKeys.SEARCH_SUGGESTIONS, [props.keyword, regionStore.currentRegion?.region_id]),
  async () => {
    if (!props.keyword) return []
    const apiParams = new URLSearchParams()
    apiParams.append('region_id', `${regionStore.currentRegion?.region_id}`)
    apiParams.append('keyword', props.keyword)
    if (categoryId.value) apiParams.append('category_id', `${categoryId.value}`)
    clearHighlight()

    const { data } = await getSearchSuggestions(Object.fromEntries(apiParams.entries()))
    return data.completes
  },
  { watch: [() => props.keyword] }
)

const suggestionUIList = computed<SearchRecordItem[]>(() =>
  props.keyword
    ? [
        { type: SearchType.KEYWORD, text: props.keyword, image: '' },
        ...(suggestionList.value?.map(s => ({ ...s, image: `${config.public.funnowCdnBaseUrl}${s.icon}` })) || []),
      ]
    : []
)

const hasAnyHint = computed(() => {
  // if (suggestionFetchStatus.value === 'idle' && !suggestionUIList.value.length) return false
  return !!popularKeywordList.value?.length || !!recordList.value.length || !!suggestionUIList.value.length
})

const hasSuggestion = computed(() => !!props.keyword && isOnline.value)

const handleClearSearchRecord = () => {
  searchRecord.clear(categoryId.value)
  emit('clear', hasAnyHint.value)
}

function emitSearchClick(item: ElasticsearchCompleteKeyword, keywordSource?: SearchKeywordSource) {
  emit('search-click', { item, keywordSource: keywordSource ?? '' })
}

function onPopularKeywordClick(keyword: string) {
  emitSearchClick({ text: keyword, type: SearchType.KEYWORD }, SearchKeywordSource.POPULAR)
}

function onHistoryKeywordClick(item: ElasticsearchCompleteKeyword) {
  emitSearchClick(item, SearchKeywordSource.HISTORY)
}

function onSuggestionClick(item: ElasticsearchCompleteKeyword) {
  emitSearchClick(item)
}
</script>
